import {useCallback} from 'react'
import {useLazyGetLatestOffersQuery} from '../services/ibeApi'
import {useAppDispatch, useAppSelector} from '../store/hooks'
import {
  hideCart,
  hideItemEditor,
  selectCartItems,
  selectPropertyId
} from '../store/slices/shoppingCartSlice'
import {
  showCartValidations,
  validateCart
} from '../store/slices/shoppingCartValidationSlice'
import {
  buildLatestOffersRequest,
  validateShoppingCart
} from '../services/cartValidation'
import {getLogger} from '../services/logging'
import {getRootState} from '../store/store'
import {selectPropertyById} from '../store/slices/configSlice'

const logger = getLogger('useCartValidator')

export const useCartValidator = () => {
  const dispatch = useAppDispatch()
  const [triggerFetch] = useLazyGetLatestOffersQuery()
  const propertyId = useAppSelector(selectPropertyId)
  const propertyConfig = useAppSelector(selectPropertyById(propertyId))

  const validate = useCallback(async (): Promise<boolean> => {
    const shoppingCartItems = selectCartItems(getRootState())

    logger.info({
      message: 'Executing cart validation...',
      data: {
        timestamp: new Date().toISOString(),
        shoppingCartItems
      }
    })

    if (!shoppingCartItems?.length) {
      logger.info({message: 'No shopping cart items to validate'})
      return true
    }

    try {
      const request = buildLatestOffersRequest(
        shoppingCartItems,
        propertyConfig?.timeZone as string
      )
      logger.info({message: 'Validating cart...', data: {request}})

      const response = await triggerFetch(request)
      const latestOffers = response.data

      logger.info({
        message: 'Successfully retrieved latest offers for validation',
        data: {latestOffers}
      })

      const validationErrors = validateShoppingCart(
        shoppingCartItems,
        latestOffers,
        propertyConfig?.timeZone as string
      )

      if (validationErrors.length > 0) {
        logger.info({
          message: 'Validation errors found',
          data: {validationErrors, latestOffers, shoppingCartItems}
        })
        dispatch(
          validateCart({
            validationErrors,
            latestOffers,
            lastValidated: new Date().toISOString()
          })
        )
        // hide other modals if open
        dispatch(hideCart())
        dispatch(hideItemEditor())

        // show validation errors
        dispatch(showCartValidations())
        return false
      } else {
        logger.info({
          message: 'No validation errors found',
          data: {latestOffers, shoppingCartItems}
        })
        dispatch(
          validateCart({
            validationErrors: [],
            latestOffers,
            lastValidated: new Date().toISOString()
          })
        )
        return true
      }
    } catch (error) {
      logger.error({message: 'Error during cart validation', data: {error}})
      // default to no validation errors
      return true
    }
  }, [triggerFetch, dispatch])

  return {validate}
}

export default useCartValidator
